import { supabase, toError } from './supabase';
import type { SupportedCurrency } from '../utils/currency';

const staticPricingCatalogJson = require('./generated/pricingCatalog.json');

export type PricingPlanId = 'starter' | 'plus' | 'pro';
export type PricingInterval = 'monthly' | 'yearly';

export type PricingCatalogFeature = {
    id: string;
    name: string;
    description: string | null;
    value: string;
    value_type: 'integer' | 'boolean';
    included: boolean;
    text: string | null;
};

export type PricingCatalogPrice = {
    lookup_key: string;
    stripe_price_id: string;
    interval: PricingInterval;
    billed_interval: 'month' | 'year';
    amount_minor_units: Record<SupportedCurrency, number>;
    display_monthly_minor_units: Record<SupportedCurrency, number>;
};

export type PricingCatalogPlan = {
    id: PricingPlanId;
    name: string;
    description: string;
    badge: string | null;
    display_order: number;
    checkout_lookup_keys: Record<PricingInterval, string>;
    prices: Record<PricingInterval, PricingCatalogPrice>;
    features: PricingCatalogFeature[];
};

export type PricingAccountOffer = {
    id: string;
    kind: 'percentage_forever';
    percent_off: number;
    starts_at: string;
    expires_at: string | null;
    billing_mode: 'immediate' | 'trial';
    discounted_plan_ids: PricingPlanId[];
};

export type PricingAccountProject = {
    id: string;
    name: string;
    recent_activity_at: string | null;
    visibility?: 'public' | 'private' | 'unlisted' | null;
};

export type PricingPlanProjectImpact = {
    max_projects: number;
    kept_project_ids: string[];
    paused_project_ids: string[];
    made_public_project_ids: string[];
};

export type PricingAccountProjectContext = {
    active_project_count: number;
    paused_project_count?: number;
    recommended_plan_id: PricingPlanId | null;
    active_projects: PricingAccountProject[];
    plan_impacts: Record<PricingPlanId, PricingPlanProjectImpact>;
};

export type PricingCatalog = {
    version: string;
    generated_at: string;
    supported_currencies: SupportedCurrency[];
    default_currency: SupportedCurrency;
    tax_behavior: 'exclusive';
    copy: {
        title: string;
        subtitle: string;
        annual_savings_label: string;
        tax_note: string;
    };
    plans: PricingCatalogPlan[];
    account_offer_status?: 'not_authenticated' | 'none' | 'available' | 'unavailable';
    account_offer?: PricingAccountOffer | null;
    account_project_context?: PricingAccountProjectContext | null;
};

function isRecord(value: unknown): value is Record<string, unknown> {
    return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function isPricingPlanId(value: unknown): value is PricingPlanId {
    return value === 'starter' || value === 'plus' || value === 'pro';
}

function isUuid(value: unknown): value is string {
    return typeof value === 'string'
        && /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);
}

function isStringArray(value: unknown): value is string[] {
    return Array.isArray(value) && value.every((item) => typeof item === 'string');
}

function hasUniqueStrings(values: string[]): boolean {
    return new Set(values).size === values.length;
}

function isValidAccountProject(value: unknown): value is PricingAccountProject {
    if (!isRecord(value)) return false;
    return typeof value.id === 'string'
        && value.id.length > 0
        && typeof value.name === 'string'
        && (value.recent_activity_at === null || typeof value.recent_activity_at === 'string');
}

function isValidPlanImpact(value: unknown): value is PricingPlanProjectImpact {
    if (!isRecord(value)) return false;
    return Number.isInteger(value.max_projects)
        && Number(value.max_projects) >= 0
        && isStringArray(value.kept_project_ids)
        && isStringArray(value.paused_project_ids)
        && isStringArray(value.made_public_project_ids);
}

function assertAccountOfferContract(catalog: Partial<PricingCatalog>): void {
    if (catalog.account_offer_status !== 'available') return;

    const offer = catalog.account_offer;
    const context = catalog.account_project_context;
    const catalogPlanIds = (catalog.plans ?? []).map((plan) => plan.id);
    if (
        !isRecord(offer)
        || !isUuid(offer.id)
        || offer.kind !== 'percentage_forever'
        || !Number.isFinite(offer.percent_off)
        || Number(offer.percent_off) <= 0
        || Number(offer.percent_off) > 100
        || (offer.billing_mode !== 'immediate' && offer.billing_mode !== 'trial')
        || !Array.isArray(offer.discounted_plan_ids)
        || offer.discounted_plan_ids.length === 0
        || !offer.discounted_plan_ids.every(isPricingPlanId)
        || !hasUniqueStrings(offer.discounted_plan_ids as string[])
        || !offer.discounted_plan_ids.every((planId) => catalogPlanIds.includes(planId))
        || typeof offer.starts_at !== 'string'
        || !Number.isFinite(Date.parse(offer.starts_at))
        || (offer.expires_at !== null && (
            typeof offer.expires_at !== 'string'
            || !Number.isFinite(Date.parse(offer.expires_at))
        ))
        || !isRecord(context)
        || !Array.isArray(context.active_projects)
        || !context.active_projects.every(isValidAccountProject)
        || !Number.isInteger(context.active_project_count)
        || Number(context.active_project_count) !== context.active_projects.length
        || (context.recommended_plan_id !== null && !catalogPlanIds.includes(context.recommended_plan_id as PricingPlanId))
        || !isRecord(context.plan_impacts)
    ) {
        throw new Error('Invalid account offer pricing response');
    }

    const activeProjectIds = context.active_projects.map((project) => project.id);
    const activeProjectIdSet = new Set(activeProjectIds);
    if (!hasUniqueStrings(activeProjectIds)) {
        throw new Error('Invalid account offer project list response');
    }

    for (const plan of catalog.plans ?? []) {
        const impact = context.plan_impacts[plan.id];
        if (!isPricingPlanId(plan.id) || !isValidPlanImpact(impact)) {
            throw new Error('Invalid account offer project impact response');
        }
        const kept = impact.kept_project_ids;
        const paused = impact.paused_project_ids;
        const madePublic = impact.made_public_project_ids;
        const expectedMaxProjects = Number.parseInt(
            plan.features.find((feature) => feature.id === 'max_projects')?.value ?? '',
            10,
        );
        if (
            !hasUniqueStrings(kept)
            || !hasUniqueStrings(paused)
            || !hasUniqueStrings(madePublic)
            || kept.some((projectId) => !activeProjectIdSet.has(projectId))
            || paused.some((projectId) => !activeProjectIdSet.has(projectId) || kept.includes(projectId))
            || madePublic.some((projectId) => !kept.includes(projectId))
            || kept.length + paused.length !== activeProjectIds.length
            || impact.max_projects !== expectedMaxProjects
            || kept.length > impact.max_projects
        ) {
            throw new Error('Invalid account offer project impact response');
        }
    }
}

function assertPricingCatalog(value: unknown, requireAccountOfferContract = false): PricingCatalog {
    const catalog = value as Partial<PricingCatalog> | null | undefined;
    if (!catalog || !Array.isArray(catalog.plans)) {
        throw new Error('Invalid pricing catalog response');
    }

    if (requireAccountOfferContract) {
        assertAccountOfferContract(catalog);
    }

    return catalog as PricingCatalog;
}

export function getStaticPricingCatalog(): PricingCatalog | null {
    try {
        return assertPricingCatalog(staticPricingCatalogJson);
    } catch (error) {
        console.error('Invalid static pricing catalog:', error);
        return null;
    }
}

export async function fetchPricingCatalog(options: {
    includeAccountOffer?: boolean;
} = {}): Promise<PricingCatalog> {
    const { data, error } = await supabase.functions.invoke<PricingCatalog>('pricing-catalog', {
        method: options.includeAccountOffer ? 'POST' : 'GET',
        ...(options.includeAccountOffer ? { body: { include_account_offer: true } } : {}),
    });

    if (error) {
        throw toError(error);
    }

    return assertPricingCatalog(data, options.includeAccountOffer === true);
}
